import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import type { TypographyCatalog } from '../../components/typography/TypographyField';
import { fetchData } from '../../helpers/apiUtils';
import TypographyPanel, { type TypographyTokens } from './TypographyPanel';

/**
 * Zentrale Typografie je Design-Scope — die Ebene über den Seiten-Overrides.
 *
 * Ein Scope ist NICHT dasselbe wie `design_variant`: Bestätigungs- und Aufzeichnungsseiten haben
 * je genau ein Template, nur Anmeldeseiten haben Varianten. Die sechs Schlüssel kommen deshalb
 * vom Server, samt Katalog — hier wird nichts davon nachgebaut.
 */

const SCOPE_LABELS: Record<string, string> = {
  doi: 'Bestätigungsseite (DOI)',
  recording: 'Aufzeichnungsseite',
  'signup:electric': 'Anmeldung · Dark Electric 2.0',
  'signup:dossier': 'Anmeldung · Dossier',
  'signup:show': 'Anmeldung · Show',
  'signup:bp': 'Anmeldung · Best Practice (Test-Arm)',
  order: 'Bestellseite',
};

interface DefaultRow {
  scope_key: string;
  configured: boolean;
  tokens: TypographyTokens;
  catalog: TypographyCatalog;
  updated_by: string | null;
  updated_at: string | null;
}

const TypographyDefaultsTab: React.FC = () => {
  const [rows, setRows] = useState<DefaultRow[] | null>(null);
  const [draft, setDraft] = useState<Record<string, TypographyTokens>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    const res = await fetchData<DefaultRow[]>('/api/webinar-lp/typography-defaults');
    if (res.success && res.data) {
      setRows(res.data);
      setDraft(Object.fromEntries(res.data.map((r) => [r.scope_key, r.tokens])));
      setMsg(null);
    } else {
      setMsg({ severity: 'error', text: res.error || 'Laden fehlgeschlagen' });
      // Nicht auf [] setzen: leer hiesse „sechs Scopes, alle ohne Werte" und waere eine Aussage
      // ueber den Bestand, die wir gerade nicht treffen koennen.
      setRows(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (scope: string) => {
    setBusy(scope);
    setMsg(null);
    const res = await fetchData<{ tokens: TypographyTokens }>(
      `/api/webinar-lp/typography-defaults/${encodeURIComponent(scope)}`,
      { method: 'PUT', body: JSON.stringify({ tokens: draft[scope] || {} }) },
    );
    setBusy(null);
    if (!res.success) {
      setMsg({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
      return;
    }
    setMsg({ severity: 'success', text: `${SCOPE_LABELS[scope] || scope} gespeichert.` });
    load();
  };

  if (rows === null) {
    return (
      <Box p={4} textAlign="center">
        {msg ? <Alert severity={msg.severity}>{msg.text}</Alert> : <CircularProgress size={28} />}
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} maxWidth={980}>
      <Alert severity="info">
        Diese Werte gelten für <strong>alle</strong> Seiten des jeweiligen Designs. Eine einzelne
        Seite kann im eigenen Editor davon abweichen. Leere Felder = das Stylesheet der Variante
        bleibt unangetastet — solange hier nichts gesetzt ist, rendert jede Seite exakt wie bisher.
        Bearbeitung ist Admins vorbehalten.
      </Alert>
      {msg && <Alert severity={msg.severity}>{msg.text}</Alert>}

      {rows.map((row) => (
        <Paper key={row.scope_key} sx={{ p: 2, backgroundColor: '#ffffff' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" sx={{ color: 'black' }}>
              {SCOPE_LABELS[row.scope_key] || row.scope_key}
            </Typography>
            <Chip
              size="small"
              label={row.configured ? 'gesetzt' : 'unverändert'}
              color={row.configured ? 'primary' : 'default'}
            />
            {row.updated_at && (
              <Typography variant="caption" color="text.secondary">
                zuletzt {row.updated_at.slice(0, 16).replace('T', ' ')} von{' '}
                {row.updated_by || 'unbekannt'}
              </Typography>
            )}
          </Box>
          <TypographyPanel
            value={draft[row.scope_key] || {}}
            onChange={(next) => setDraft((prev) => ({ ...prev, [row.scope_key]: next }))}
            catalog={row.catalog}
            // Auf dieser Ebene gibt es nichts zu erben — der Scope-Default IST die Basis.
            defaults={{}}
            scope={row.scope_key}
          />
          <Button
            variant="contained"
            sx={{ mt: 1.5 }}
            disabled={busy !== null}
            onClick={() => save(row.scope_key)}
          >
            {busy === row.scope_key ? 'Speichert…' : 'Default speichern'}
          </Button>
        </Paper>
      ))}
    </Box>
  );
};

export default TypographyDefaultsTab;
